import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, ASSET_TYPE_LABELS, ENTITY_TYPE_LABELS, OWNER_LABELS, CONFIDENCE_OPTS, REVIEW_OPTS, assetColor, assetTint } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { useDemo } from "@/context/DemoContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Coins, PencilSimple, Trash, CheckCircle, WarningCircle, ShieldCheck, ArrowsClockwise, House } from "@phosphor-icons/react";
import { AddressFields, composeAddress, emptyAddress } from "@/components/AddressFields";
import { BankLedgerConnect } from "@/components/BankLedgerConnect";
import { toast } from "sonner";

const empty = { name: "", asset_type: "real_estate", entity_id: null, owner_party: "joint", address: "", address_parts: { ...emptyAddress }, current_value: "", value_as_of: "", value_source: "manual", ownership_pct: 100, confidence: "medium", review_status: "needs_review", notes: "" };

const reviewBadge = (s) => {
  if (s === "confirmed") return { label: "Confirmed", cls: "bg-success/15 text-success", icon: CheckCircle };
  if (s === "unverified") return { label: "Unverified", cls: "bg-destructive/15 text-destructive", icon: WarningCircle };
  return { label: "Needs review", cls: "bg-warning/15 text-warning", icon: WarningCircle };
};
const confColor = (c) => (c === "high" ? "text-success" : c === "low" ? "text-destructive" : "text-warning");

export default function Assets() {
  const { fmtMoney, fmtAsset } = useDemo();
  const { hid, isOperator, ownerLabels, ownerLabel, myOwnerParty, flaggedEntityIds, delinquentAssetIds } = useHousehold();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [entities, setEntities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [origValue, setOrigValue] = useState(null);
  const [overrideAsset, setOverrideAsset] = useState(null);
  const [entityDlgOpen, setEntityDlgOpen] = useState(false);
  const [entityForm, setEntityForm] = useState({ name: "", type: "llc", owner_party: "joint" });
  const [savingEntity, setSavingEntity] = useState(false);

  const quickAddEntity = async () => {
    if (!entityForm.name.trim()) { toast.error("Entity name required"); return; }
    setSavingEntity(true);
    try {
      const res = await api.post(`/entities?household_id=${hid}`, entityForm);
      const newEnt = res.data;
      const list = await api.get(`/entities?household_id=${hid}`);
      setEntities(list.data);
      setForm((f) => ({ ...f, entity_id: newEnt.id }));
      toast.success(`Added entity "${newEnt.name}" and selected it`);
      setEntityDlgOpen(false);
      setEntityForm({ name: "", type: "llc", owner_party: myOwnerParty });
    } catch (e) { toast.error("Could not add entity"); }
    finally { setSavingEntity(false); }
  };

  const load = useCallback(() => {
    if (!hid) return;
    api.get(`/assets?household_id=${hid}`).then((r) => setAssets(r.data)).catch(() => {});
    api.get(`/entities?household_id=${hid}`).then((r) => setEntities(r.data)).catch(() => {});
  }, [hid]);
  useEffect(() => { load(); }, [load]);

  const entName = (id) => entities.find((e) => e.id === id)?.name || "—";

  const openCreate = () => { setForm(empty); setEditId(null); setOrigValue(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...empty, ...a, current_value: a.current_value ?? "", value_as_of: a.value_as_of || "", entity_id: a.entity_id || null, address_parts: a.address_parts || { ...emptyAddress, street: a.address || "" } }); setEditId(a.id); setOrigValue(a.current_value ?? null); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Asset name is required"); return; }
    const newVal = form.current_value === "" ? 0 : Number(form.current_value);
    // A value the user types/changes here is a manually-entered (preferred) value.
    const valueChanged = !editId || origValue == null || Number(newVal) !== Number(origValue);
    const payload = { ...form, current_value: newVal, ownership_pct: Number(form.ownership_pct) || 100, value_as_of: form.value_as_of || null, entity_id: form.entity_id || null, value_source: valueChanged ? "manual" : (form.value_source || "manual") };
    try {
      if (editId) await api.put(`/assets/${editId}?household_id=${hid}`, payload);
      else await api.post(`/assets?household_id=${hid}`, payload);
      toast.success(editId ? "Asset updated" : "Asset added");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    await api.delete(`/assets/${id}?household_id=${hid}`); toast.success("Deleted"); load();
  };

  const [refreshing, setRefreshing] = useState(null);
  const requestRefresh = (a) => {
    const isManual = (a.value_source || "manual") === "manual";
    if (isManual && Number(a.current_value) > 0) setOverrideAsset(a);
    else doRefresh(a);
  };
  const doRefresh = async (a) => {
    setOverrideAsset(null);
    setRefreshing(a.id);
    try {
      const res = await api.post(`/assets/${a.id}/refresh-value?household_id=${hid}`);
      toast.success(`Updated to ${fmtAsset(res.data.current_value)} (Zillow-style estimate)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not fetch market value"); }
    finally { setRefreshing(null); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Portfolio</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Assets</h1>
        </div>
        {isOperator && <Button data-testid="add-asset-button" onClick={openCreate} className="rounded-sm font-semibold"><Plus size={16} weight="bold" className="mr-1" /> Add Asset</Button>}
      </div>

      {isOperator && <BankLedgerConnect hid={hid} onChange={load} />}

      {assets.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Coins size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-display text-xl font-bold">No assets yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add your real estate, mineral royalties, LP interests, accounts and more.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assets.map((a) => {
            const rb = reviewBadge(a.review_status);
            const delinquent = delinquentAssetIds?.has(a.id);
            return (
              <div key={a.id} data-testid={`asset-card-${a.id}`} onClick={() => navigate(a.asset_type === "real_estate" ? `/properties/${a.id}` : `/assets/${a.id}`)} className="bg-card border border-border rounded-sm group cursor-pointer shadow-[0_4px_20px_hsl(var(--ploy-ink)/0.04)] hover:shadow-[0_8px_30px_hsl(var(--ploy-ink)/0.08)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 overflow-hidden" style={{ borderTop: `2px solid ${assetColor(a.asset_type)}` }}>
                {/* Primary-doc cover thumbnail (non-RE assets with a PDF uploaded) */}
                {a.primary_doc && (
                  <div className="w-full h-36 bg-muted overflow-hidden">
                    <img
                      data-testid={`asset-card-thumb-${a.id}`}
                      src={`${API}/assets/${a.id}/primary-doc-thumb?household_id=${hid}`}
                      alt="Document cover"
                      className="w-full h-full object-cover object-top"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                )}
                <div className="p-5">
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm" style={{ background: assetTint(a.asset_type), color: assetColor(a.asset_type) }}>{ASSET_TYPE_LABELS[a.asset_type] || a.asset_type}</span>
                  {isOperator && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button data-testid={`edit-asset-${a.id}`} onClick={(e) => { e.stopPropagation(); openEdit(a); }} className="text-muted-foreground hover:text-foreground"><PencilSimple size={15} /></button>
                      <button data-testid={`delete-asset-${a.id}`} onClick={(e) => { e.stopPropagation(); remove(a.id); }} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                    </div>
                  )}
                </div>
                <h3 className="font-display text-lg font-bold mt-3 tracking-tight">{a.name}</h3>
                <p className="font-mono text-2xl font-black tabular-nums mt-1 asset-value">{fmtAsset(a.current_value)}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  {a.entity_id && flaggedEntityIds?.has(a.entity_id) && (
                    <span data-testid={`asset-entity-alert-${a.id}`} title="Entity renewal due" className="h-2 w-2 rounded-full bg-destructive shrink-0" />
                  )}
                  <span>{entName(a.entity_id)} · {ownerLabel(a.owner_party)}{a.ownership_pct != 100 ? ` · ${a.ownership_pct}%` : ""}</span>
                </p>
                {delinquent && (
                  <p data-testid={`asset-tax-delinquent-${a.id}`} className="text-xs mt-2 inline-flex items-center gap-1.5 text-destructive font-semibold">
                    <WarningCircle size={13} weight="fill" /> Property tax delinquent
                  </p>
                )}
                {a.meta?.avm?.low != null && (
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Low end of {fmtAsset(a.meta.avm.low)}–{fmtAsset(a.meta.avm.high)} estimate · via Zillow market data{a.value_as_of ? ` · ${a.value_as_of}` : ""}
                  </p>
                )}
                {a.asset_type === "real_estate" && isOperator && (
                  <button
                    data-testid={`refresh-value-${a.id}`}
                    onClick={(e) => { e.stopPropagation(); requestRefresh(a); }}
                    disabled={refreshing === a.id}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/70 transition-colors disabled:opacity-50"
                  >
                    <ArrowsClockwise size={13} weight="bold" className={refreshing === a.id ? "animate-spin" : ""} />
                    {refreshing === a.id ? "Fetching estimate…" : a.meta?.avm ? "Refresh market value" : "Get market value from Zillow"}
                  </button>
                )}
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs">
                  <span className={`inline-flex items-center gap-1 font-semibold px-1.5 py-0.5 rounded-sm ${rb.cls}`}><rb.icon size={12} weight="fill" /> {rb.label}</span>
                  <span className={`inline-flex items-center gap-1 font-semibold ${confColor(a.confidence)}`}><ShieldCheck size={12} weight="fill" /> {a.confidence}</span>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">{editId ? "Edit Asset" : "New Asset"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">Name</Label><Input data-testid="asset-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" /></div>
            {form.asset_type === "real_estate" && (
              <div><Label className="text-xs uppercase tracking-wider font-bold">Property Address <span className="text-muted-foreground font-normal normal-case">(for Zillow market value)</span></Label>
                <div className="mt-1.5"><AddressFields value={form.address_parts} idPrefix="asset-address" onChange={(p) => setForm({ ...form, address_parts: p, address: composeAddress(p) })} /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">Asset Type</Label>
                <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}><SelectTrigger data-testid="asset-type-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ASSET_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">Owned by Entity</Label>
                <Select value={form.entity_id || "none"} onValueChange={(v) => { if (v === "__add__") { setEntityForm({ name: "", type: "llc", owner_party: myOwnerParty }); setEntityDlgOpen(true); } else { setForm({ ...form, entity_id: v === "none" ? null : v }); } }}><SelectTrigger data-testid="asset-entity-select" className="rounded-sm mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    <SelectItem value="__add__" data-testid="asset-entity-add-option" className="text-primary font-semibold">+ Add new entity…</SelectItem>
                  </SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">Current Value</Label><Input data-testid="asset-value-input" type="number" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} className="rounded-sm mt-1 font-mono" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">Value as of</Label><Input data-testid="asset-asof-input" type="date" value={form.value_as_of || ""} onChange={(e) => setForm({ ...form, value_as_of: e.target.value })} className="rounded-sm mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">Owner</Label>
                <Select value={form.owner_party} onValueChange={(v) => setForm({ ...form, owner_party: v })}><SelectTrigger data-testid="asset-owner-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ownerLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">Ownership %</Label><Input type="number" value={form.ownership_pct} onChange={(e) => setForm({ ...form, ownership_pct: e.target.value })} className="rounded-sm mt-1 font-mono" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">Confidence</Label>
                <Select value={form.confidence} onValueChange={(v) => setForm({ ...form, confidence: v })}><SelectTrigger data-testid="asset-confidence-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONFIDENCE_OPTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Review status</Label>
              <Select value={form.review_status} onValueChange={(v) => setForm({ ...form, review_status: v })}><SelectTrigger data-testid="asset-review-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{REVIEW_OPTS.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="save-asset-button" onClick={save} className="rounded-sm font-semibold w-full">{editId ? "Save Changes" : "Add Asset"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!overrideAsset} onOpenChange={(o) => !o && setOverrideAsset(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black flex items-center gap-2"><WarningCircle size={22} weight="fill" className="text-warning" /> Override manual value?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{overrideAsset?.name}</span> has a manually entered value of{" "}
            <span className="font-mono font-semibold text-foreground">{fmtAsset(overrideAsset?.current_value)}</span>, which is treated as your preferred amount.
            Do you really want to replace it with the latest Zillow market estimate?
          </p>
          <DialogFooter className="gap-2">
            <Button data-testid="override-cancel-button" variant="outline" onClick={() => setOverrideAsset(null)} className="rounded-sm font-semibold flex-1">Keep my value</Button>
            <Button data-testid="override-confirm-button" onClick={() => doRefresh(overrideAsset)} className="rounded-sm font-semibold flex-1">Yes, override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={entityDlgOpen} onOpenChange={setEntityDlgOpen}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl font-black">Add New Entity</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">Name</Label><Input data-testid="quick-entity-name-input" value={entityForm.name} onChange={(e) => setEntityForm({ ...entityForm, name: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Foyco Holdings LLC" autoFocus /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">Type</Label>
                <Select value={entityForm.type} onValueChange={(v) => setEntityForm({ ...entityForm, type: v })}><SelectTrigger data-testid="quick-entity-type-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ENTITY_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">Owner</Label>
                <Select value={entityForm.owner_party} onValueChange={(v) => setEntityForm({ ...entityForm, owner_party: v })}><SelectTrigger data-testid="quick-entity-owner-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ownerLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="quick-entity-save-button" onClick={quickAddEntity} disabled={savingEntity} className="rounded-sm font-semibold w-full">{savingEntity ? "Adding…" : "Add & Select"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
